import { AfterViewInit, Component, OnDestroy, effect, input, output, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";

import { NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { AuthorizationService } from '@myapp/modules/fwcore/services';
import { CheckBoxModule } from "@progress/kendo-angular-inputs";
import { ActionName, ListBoxComponent, ListBoxModule, ListBoxSelectionEvent } from "@progress/kendo-angular-listbox";
import { ControlResizerOptions, ControlResizerService } from "../../../../../../services/framework";
import { RequestUserPermission } from "../../../../../modules/fwcore";
import { Module, ModuleChecked } from "../../../models";
import { RequestModule } from "../../../models/messages";
import { AdminService } from '../../../services';

@Component({
  selector: 'app-modules-list',
  templateUrl: './modules-list.component.html',
  styleUrls: ['./modules-list.component.scss'],
  standalone: true,
  imports: [ListBoxModule, NgIf, FormsModule, CheckBoxModule]
})

export class ModulesListComponent implements AfterViewInit, OnDestroy {

  //--------------------  Input / Output Properties  -------------------

  /** License id used to filter the modules fetch. */
  public licenseId = input<string | null>(null);

  /** User id context for the list. */
  public userId = input<string | null>(null);

  /** Filters modules by api availability. */
  public hasApi = input<boolean | null>(null);

  /** Filters modules by active state. */
  public active = input<boolean | null>(null);

  /** Includes host modules in the fetch when true. */
  public includeHost = input<boolean | null>(null);

  /** Shows the checkbox column when true. */
  public showCheckbox = input(false);

  /** Retained for legacy callers; sizing is now pane-driven */
  public minHeight = input(200);

  /** Retained for legacy callers; sizing is now pane-driven */
  public widthThreshold = input(600);

  /** Total height the component may occupy */
  public heightControl = input(300);

  /** Ids of items to mark as checked; applied to the loaded items. */
  public checkedIds = input<string[]>([]);

  /** Emits the selected module, or null when selection clears. */
  public selected = output<Module | null>();

  /** Emits the number of items loaded. */
  public dataLoaded = output<number | null>();

  //----------------------  ViewChild Properties  ----------------------

  /** Reference to the kendo listbox. */
  protected listbox = viewChild.required(ListBoxComponent);

  //------------------------  Public Properties  -----------------------

  /** Currently selected module. */
  public selectedItem?: Module | null;

  //------------------------  State Properties  ------------------------

  /** License id applied to the last modules fetch */
  private currentLicenseId: string | null = null;

  /** Modules displayed in the listbox. */
  protected dataItems: ModuleChecked[] = [];

  /** Emits on component destruction to complete open subscriptions. */
  protected destroy$ = new Subject();

  /** True once a caller has initialized the list. */
  protected hasInitialized: boolean = false;

  /** True while data is loading. */
  protected loading: boolean = true;

  //----------------------  Permission Properties  ---------------------

  /** Permission request payload for the current route. */
  protected reqPerm: RequestUserPermission = {

  }

  //---------------------------  Constructor  --------------------------

  constructor(
    public serviceAuth: AuthorizationService
    , protected router: Router
    , protected adminService: AdminService
  ) {

    this.setupEffectHandlers();
  }

  //-------------------------  Public Methods  -------------------------

  /** Marks the list as initialized and loads the modules. */
  public Initialized(): void {

    this.hasInitialized = true;
    this.getModules();
  }

  /** Clears the current selection. */
  public clearSelection() {

    this.selectedItem = null;
    this.listbox().clearSelection();
  }

  /** Returns the ids of the currently checked items. */
  public getCheckedIds(): string[] {

    const checkedItemIds: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.id!);

    return checkedItemIds ?? [];
  }

  /** Applies the supplied ids as the checked items. */
  public setCheckedIds(ids: string[]): void {

    this.dataItems.forEach((item) => {
      item.checked = ids.includes(item.id!);
    });
  }

  //------------------------  Lifecycle Methods  -----------------------

  /**
   * Captures the current route for permission requests
   */
  ngAfterViewInit(): void {

    this.reqPerm.route = this.router.url;
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Reacts to input changes; replaces the former ngOnChanges wiring. */
  protected setupEffectHandlers(): void {

    effect(() => {

      const licenseId = this.licenseId();

      if (licenseId === this.currentLicenseId) {
        return;
      }

      this.currentLicenseId = licenseId;

      if (!this.hasInitialized) {
        return;
      }

      this.getModules();
    });

    // Applies parent-supplied checked ids to the currently loaded items;
    // replaces the former checkedIds input setter.
    effect(() => {

      this.setCheckedIds(this.checkedIds());
    });
  }

  //-------------------------  Event Handlers  -------------------------

  /** Handles listbox toolbar action clicks. */
  public onActionClick(event: ActionName): void {

    const index: number = this.listbox().selectedIndex;
  }

  /** Handles listbox selection changes. */
  public onSelectionEvent(event: ListBoxSelectionEvent): void {

    this.setSelectedItem(event.index);
  }

  //--------------------------  List Methods  --------------------------

  /** Sets the selected item from the listbox index and emits it. */
  protected setSelectedItem(index: number) {

    this.selectedItem = this.listbox().data[index];
    this.selected.emit(this.selectedItem ?? null);
  }

  //---------------------------  Api Methods  --------------------------

  /** Fetches the modules for the current filters and selects the first item. */
  protected getModules(): void {

    this.loading = true;

    const req: RequestModule = {
      licenseId: this.licenseId()
      , active: this.active()
      , hasApi: this.hasApi()
      , includeHost: this.includeHost()
    };

    this.adminService.getModules$(req)
      .pipe(takeUntil(this.destroy$), tap(() => this.loading = false))
      .subscribe(
        resp => {

          this.dataItems = resp.data!;
          this.dataLoaded.emit(this.dataItems?.length);

          // by default select the first item
          if (this.dataItems.length > 0) {
            this.listbox().selectItem(0);
            this.selectedItem = this.dataItems[0];
          }
          else
            this.selectedItem = null;

          this.selected.emit(this.selectedItem);
        }
      );

  }

}
